"use client";
import { Dialog, useDialogContext } from "@ark-ui/react/dialog";
import { ark } from "@ark-ui/react/factory";
import { type ComponentProps, forwardRef } from "react";
import { createStyleContext, styled } from "@repo/styled-system/jsx";
import { dialog } from "@repo/styled-system/recipes";

const { withRootProvider, withContext } = createStyleContext(dialog);

/**
 * Focus contract, made EXPLICIT (features/accessibility.md — Ark Dialog focus config). These are Ark's
 * defaults, pinned here so the design-system's modal behaviour is guaranteed and self-documented rather
 * than implicit — a consumer can still override any of them per instance (defaults merge first):
 *  - `trapFocus`: focus is trapped inside the dialog while open (WCAG 2.4.3 / 2.1.2 — no escape to page)
 *  - `restoreFocus`: on close, focus RETURNS to the element that opened it (the return-focus requirement)
 *  - `closeOnEscape`: Escape closes (keyboard operability)
 *  - `closeOnInteractOutside`: a click on the backdrop closes
 *  - `modal`: the rest of the page is inert + `aria-hidden` to assistive tech
 * `initialFocus` is left to Ark's default (first focusable / the content) so each dialog focuses its own
 * most-appropriate control; a consumer needing a specific target passes `initialFocusEl`.
 */
const focusDefaults = {
  trapFocus: true,
  restoreFocus: true,
  closeOnEscape: true,
  closeOnInteractOutside: true,
  modal: true,
  unmountOnExit: true,
  lazyMount: true,
} as const;

export type RootProps = ComponentProps<typeof Root>;
export const Root = withRootProvider(Dialog.Root, {
  defaultProps: { ...focusDefaults },
});
export const RootProvider = withRootProvider(Dialog.RootProvider, {
  defaultProps: { ...focusDefaults },
});
export const Backdrop = withContext(Dialog.Backdrop, "backdrop");
export const CloseTrigger = withContext(Dialog.CloseTrigger, "closeTrigger");
export const Content = withContext(Dialog.Content, "content");
export const Description = withContext(Dialog.Description, "description");
export const Positioner = withContext(Dialog.Positioner, "positioner");
export const Title = withContext(Dialog.Title, "title");
export const Trigger = withContext(Dialog.Trigger, "trigger");
export const Body = withContext(ark.div, "body");
export const Header = withContext(ark.div, "header");
export const Footer = withContext(ark.div, "footer");

const StyledButton = styled(ark.button);

export const ActionTrigger = forwardRef<HTMLButtonElement, ComponentProps<typeof StyledButton>>(
  function ActionTrigger(props, ref) {
    const dialogApi = useDialogContext();
    return <StyledButton {...props} ref={ref} onClick={() => dialogApi.setOpen(false)} />;
  },
);

export { DialogContext as Context } from "@ark-ui/react/dialog";
